import type {CopilotChatCommandType} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {getAnchoredPosition} from '@primer/behaviors'
import {ActionList, Overlay} from '@primer/react'
import {useCallback, useEffect, useRef, useState} from 'react'

type CommandSuggestionsProps = {
  chatCommand: CopilotChatCommandType
  suggestions: string[]
  onSelect: (command: CopilotChatCommandType, suggestion: string) => void
  anchorRef: React.RefObject<HTMLElement | null>
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
}

export const CommandSuggestions = ({
  chatCommand,
  suggestions,
  onSelect,
  anchorRef,
  textAreaRef,
}: CommandSuggestionsProps) => {
  const [top, setTop] = useState<number>(0)
  const [left, setLeft] = useState<number>(0)
  const [isOpen, setIsOpen] = useState(false)
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>(undefined)
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)
  const [textAreaValue, setTextAreaValue] = useState<string>('')
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  // Filter suggestions based on text
  const visibleSuggestions = suggestions.filter(suggestion => {
    const fullSuggestion = `/${chatCommand} ${suggestion}`.toLowerCase()
    const lowerTextAreaValue = textAreaValue.toLowerCase().trim()
    return fullSuggestion.includes(lowerTextAreaValue) && fullSuggestion !== lowerTextAreaValue
  })

  const isVisible = isOpen && visibleSuggestions.length > 0

  const updatePosition = useCallback(() => {
    if (overlayRef.current && anchorRef.current) {
      const {top: topPos, left: leftPos} = getAnchoredPosition(overlayRef.current, anchorRef.current, {
        align: 'start',
        side: 'outside-bottom',
        anchorOffset: 4,
      })
      setTop(topPos)
      setLeft(leftPos)
    }
  }, [anchorRef])

  useEffect(() => {
    // Open on the initial render only if the textArea has focus.
    if (textAreaRef.current === document.activeElement) {
      setIsOpen(true)
    }
  }, [textAreaRef])

  // Track textarea value changes
  useEffect(() => {
    const textArea = textAreaRef.current
    if (!textArea) return

    const handleInput = () => {
      setTextAreaValue(textArea.value)
    }

    // Set initial value
    setTextAreaValue(textArea.value)

    textArea.addEventListener('input', handleInput)

    return () => {
      textArea.removeEventListener('input', handleInput)
    }
  }, [textAreaRef])

  // Measure and track anchor width dynamically using ResizeObserver
  useEffect(() => {
    const currentAnchor = anchorRef.current
    if (!isVisible || !currentAnchor) return

    const updateLayout = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect()
        setAnchorWidth(rect.width)
        updatePosition()
      }
    }

    // Initial measurement
    updateLayout()

    // Create observer only if it doesn't exist
    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(updateLayout)
    }

    // Watch for size changes
    resizeObserverRef.current.observe(currentAnchor)

    return () => {
      // Unobserve when effect cleans up, but keep the observer instance
      if (resizeObserverRef.current) {
        resizeObserverRef.current.unobserve(currentAnchor)
      }
    }
  }, [isVisible, anchorRef, updatePosition])

  const shouldIgnoreInteraction = useCallback(
    (target: Node | null): boolean => {
      if (!target) return false
      // Ignore if interaction is within textarea or overlay
      return (textAreaRef.current?.contains(target) ?? false) || (overlayRef.current?.contains(target) ?? false)
    },
    [textAreaRef, overlayRef],
  )

  useEffect(() => {
    const textArea = textAreaRef.current
    if (!textArea) return

    const handleFocusOut = (event: FocusEvent) => {
      // Don't close if focus moved to overlay or stayed in textarea
      if (shouldIgnoreInteraction(event.relatedTarget as Node | null)) {
        return
      }
      // Close the overlay when focus leaves the textarea
      setIsOpen(false)
    }

    textArea.addEventListener('blur', handleFocusOut)

    return () => {
      textArea.removeEventListener('blur', handleFocusOut)
    }
  }, [textAreaRef, shouldIgnoreInteraction])

  // Reopen overlay when textarea is focused and there's a chat command
  useEffect(() => {
    const textArea = textAreaRef.current
    if (!textArea) return

    const handleFocus = () => {
      if (chatCommand) {
        setIsOpen(true)
      }
    }

    textArea.addEventListener('focus', handleFocus)

    return () => {
      textArea.removeEventListener('focus', handleFocus)
    }
  }, [textAreaRef, chatCommand])

  const closeAndReset = useCallback(() => {
    setIsOpen(false)
    setSelectedIndex(-1)
  }, [])

  // Reset selectedIndex and unlock suggestions when printable characters are typed
  useEffect(() => {
    const textArea = textAreaRef.current
    if (!textArea || !isVisible) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if it's a printable character (length 1 and not a modifier key) or delete/backspace
      if (
        (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey && !event.isComposing) ||
        event.key === 'Backspace' ||
        event.key === 'Delete'
      ) {
        setSelectedIndex(-1)
      }
    }

    textArea.addEventListener('keydown', handleKeyDown)

    return () => {
      textArea.removeEventListener('keydown', handleKeyDown)
    }
  }, [textAreaRef, isVisible])

  // Handle arrow key navigation on textarea
  useEffect(() => {
    const textArea = textAreaRef.current
    if (!textArea || !isVisible) return

    const handleArrowNavigation = (direction: 'up' | 'down', prevIndex: number) => {
      const newIndex =
        direction === 'down'
          ? prevIndex === -1
            ? 0
            : Math.min(prevIndex + 1, visibleSuggestions.length - 1)
          : prevIndex === -1
            ? visibleSuggestions.length - 1
            : Math.max(prevIndex - 1, 0)

      const suggestion = visibleSuggestions[newIndex]
      if (suggestion) {
        onSelect(chatCommand, suggestion)
      }
      return newIndex
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        const direction = event.key === 'ArrowDown' ? 'down' : 'up'
        setSelectedIndex(prev => handleArrowNavigation(direction, prev))
      }
    }

    textArea.addEventListener('keydown', handleKeyDown)

    return () => {
      textArea.removeEventListener('keydown', handleKeyDown)
    }
  }, [textAreaRef, isVisible, visibleSuggestions, chatCommand, onSelect])

  const onClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // Don't close if clicking on the textarea or overlay
      if (event.target instanceof Node && shouldIgnoreInteraction(event.target)) {
        return
      }
      closeAndReset()
    },
    [shouldIgnoreInteraction, closeAndReset],
  )

  const onItemSelect = useCallback(
    (suggestion: string) => {
      onSelect(chatCommand, suggestion)
      setSelectedIndex(-1)
    },
    [chatCommand, onSelect],
  )

  if (!isVisible) {
    return null
  }

  return (
    <Overlay
      top={top}
      left={left}
      onClickOutside={onClickOutside}
      onEscape={closeAndReset}
      returnFocusRef={anchorRef}
      aria-label="Command suggestions"
      style={anchorWidth ? {width: `${anchorWidth}px`} : undefined}
      data-testid="command-suggestions-overlay"
      ref={overlayRef}
      preventFocusOnOpen
    >
      <ActionList>
        {visibleSuggestions.map((suggestion, index) => {
          return (
            <ActionList.Item
              key={suggestion}
              size="large"
              onSelect={() => onItemSelect(suggestion)}
              active={index === selectedIndex}
            >
              <ActionList.LeadingVisual>
                <span className="fgColor-muted">/{chatCommand}</span>
              </ActionList.LeadingVisual>
              {suggestion}
            </ActionList.Item>
          )
        })}
      </ActionList>
    </Overlay>
  )
}
